import { Flex, Pill, Stack, Text, Truncate } from '@eleveo/components-react';
import { HomeIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { parseAbsolute } from '@internationalized/date';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React, { Fragment, useMemo } from 'react';

import { useAuth } from '../../app/AuthorizationProvider';
import { getCalculatedHoursMinutes } from '../../utils/date';
import { DateFormatter } from '../DateFormatter';
import { ScheduleElementType } from './types';

interface BriefScheduleOverviewProps {
  convertToZonedDate?: boolean;
  date: number;
  elements: ScheduleElementType[];
  remote?: boolean;
  t: TFunction;
  timezone: string;
  testIdSlug?: string;
}

export const BriefScheduleOverview: React.FC<BriefScheduleOverviewProps> = ({
  convertToZonedDate = true,
  date,
  elements,
  remote = false,
  t,
  testIdSlug = 'agent',
  timezone,
}) => {
  const { timezone: usersTimezone } = useAuth();

  const zonedDate = parseAbsolute(new Date(date).toISOString(), convertToZonedDate ? timezone : usersTimezone);

  const durations = useMemo(() => {
    let totalScheduleDuration = 0;
    let totalPaidDuration = 0;

    elements.forEach(({ paid, custom: { startDateTime, endDateTime } }) => {
      const start = parseAbsolute(new Date(startDateTime).toISOString(), timezone);
      const end = parseAbsolute(new Date(endDateTime).toISOString(), timezone);
      const elementDuration = end.compare(start) / 60 / 1000; // getting difference value in minutes

      totalScheduleDuration += elementDuration;

      if (paid) {
        totalPaidDuration += elementDuration;
      }
    });

    const { hours: totalHours, minutes: totalMinutes } = getCalculatedHoursMinutes(totalScheduleDuration / 60);
    const { hours: totalPaidHours, minutes: totalPaidMinutes } = getCalculatedHoursMinutes(totalPaidDuration / 60);

    return { totalHours, totalMinutes, totalPaidHours, totalPaidMinutes };
  }, [elements, timezone]);

  return (
    <Stack direction="vertical" spacing={15} testId={`${testIdSlug}:brief-overview`}>
      <Flex className="text-center bg-blue-100 p-10 rounded-sm" column testId={`${testIdSlug}:brief-overview:date`}>
        <Text weight="bold">
          <DateFormatter options={{ dateStyle: 'long' }}>{zonedDate.toDate()}</DateFormatter>
        </Text>
        <Text className="text-sm">
          (<DateFormatter options={{ weekday: 'long' }}>{zonedDate.toDate()}</DateFormatter>)
        </Text>
      </Flex>
      {remote && (
        <Pill color="success" data-test-id={`${testIdSlug}:brief-overview:working-remotely`} variant="outline">
          <HomeIcon />
          {t('schedule.workplaceType.remote')}
        </Pill>
      )}

      <div
        className="grid grid-cols-5 gap-x-20 gap-y-10 text-sm"
        data-test-id={`${testIdSlug}:brief-overview:shift-elements`}
      >
        {elements.map(
          ({ color, custom: { startDateTime, endDateTime }, key, productive, scheduleElementName }, index) => (
            <Fragment key={key}>
              <div
                className={clsx('col-span-3 leading-4', { 'font-bold': productive })}
                data-test-id={`${testIdSlug}:brief-overview:shift-element[${index}]:time`}
              >
                <DateFormatter options={{ timeStyle: 'short', timeZone: timezone }}>
                  {parseAbsolute(new Date(startDateTime).toISOString(), timezone).toDate()}
                </DateFormatter>{' '}
                -{' '}
                <DateFormatter options={{ timeStyle: 'short', timeZone: timezone }}>
                  {parseAbsolute(new Date(endDateTime).toISOString(), timezone).toDate()}
                </DateFormatter>
              </div>
              <Flex
                className={clsx('col-span-2 items-center leading-4', { 'font-bold': productive })}
                testId={`${testIdSlug}:brief-overview:shift-element[${index}]:name`}
              >
                <span className="block mie-10" style={{ borderRadius: 2, background: color, height: 10, width: 10 }} />
                <Truncate>{scheduleElementName}</Truncate>
              </Flex>
            </Fragment>
          )
        )}
      </div>

      <div className="w-fit mx-auto grid grid-cols-3 gap-y-10 text-sm leading-3" style={{ columnGap: '0.5ch' }}>
        <Text className="text-grey-550 text-end">{t('schedule.element.paid')}: </Text>
        <div className="col-span-2" data-test-id={`${testIdSlug}:brief-overview:paid-time`}>
          <Text className="font-bold mis-auto">{durations.totalPaidHours}</Text> {t('time.hrs')}{' '}
          <Text className="font-bold mie-auto">{durations.totalPaidMinutes}</Text> {t('time.min')}
        </div>
        <Text className="text-grey-550 text-end">{t('time.total')}: </Text>
        <div className="col-span-2" data-test-id={`${testIdSlug}:brief-overview:total-time`}>
          <Text className="font-bold mis-auto">{durations.totalHours}</Text> {t('time.hrs')}{' '}
          <Text className="font-bold mie-auto">{durations.totalMinutes}</Text> {t('time.min')}
        </div>
      </div>
    </Stack>
  );
};
